import { useCallback, useRef, useState } from 'react';
import { Api } from 'apiClient/Api';

interface TranscriptionResult {
    ok: boolean;
    text: string | null;
    error: string | null;
    elapsedMilliseconds: number;
}

/**
 * Diktat: aufnehmen, hochladen, Text zurück.
 *
 * Der Browser nimmt über `MediaRecorder` auf und schickt die Datei an den Server; der spricht mit
 * dem konfigurierten Dienst. **Bewusst nicht die Web Speech API**: die schickt das Audio zu Google
 * bzw. Apple, und ein Kundengespräch ist genau die Datenklasse, die den Server nicht verlassen soll.
 *
 * Der Aufnahme-Stopp liefert den Text über `onText` — das Feld füllt sich also erst nach dem
 * Loslassen, nicht live. Für „Satz diktieren" reicht das; Mithören ist ein eigenes Vorhaben.
 */
export const useDictation = (api: Api<unknown>, onText: (text: string) => void) => {
    const [recording, setRecording] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<BlobPart[]>([]);

    const stop = useCallback(() => {
        recorderRef.current?.stop();
        setRecording(false);
    }, []);

    const start = useCallback(async () => {
        setError(null);

        if (!navigator.mediaDevices?.getUserMedia) {
            setError('Dieser Browser kann nicht aufnehmen.');
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            chunksRef.current = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            recorder.onstop = async () => {
                // Mikrofon freigeben, sonst bleibt die Aufnahmeanzeige des Browsers stehen.
                stream.getTracks().forEach((t) => t.stop());

                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                if (blob.size === 0) return;

                setBusy(true);
                try {
                    const form = new FormData();
                    form.append('file', blob, 'aufnahme.webm');

                    const r = await api.request<TranscriptionResult, unknown>({
                        path: '/Speech/Transcribe',
                        method: 'POST',
                        body: form,
                        secure: true,
                        format: 'json',
                    });

                    if (r.data.ok && r.data.text) onText(r.data.text);
                    else setError(r.data.error ?? 'Die Aufnahme konnte nicht erkannt werden.');
                } catch {
                    setError('Die Aufnahme konnte nicht übertragen werden.');
                } finally {
                    setBusy(false);
                }
            };

            recorder.start();
            recorderRef.current = recorder;
            setRecording(true);
        } catch {
            // Häufigster Fall: der Nutzer hat den Mikrofonzugriff abgelehnt.
            setError('Kein Zugriff auf das Mikrofon.');
        }
    }, [api, onText]);

    return { recording, busy, error, start, stop };
};
